import React, { useEffect, useState } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import { usePatientTreatmentContext } from './ContextProvider';
import { useAppContext } from '../../../../Services/AppContext';
import ElectronicPrescriptionModal from '../../../Prescription/Components/ElectronicPrescriptionModal';
import EReceptList from '../../../../Components/eRecept/List/EReceptList';
import SearchMedicamentRecord from '../../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecord';
import SubTabNavigationStepper from '../../../../Components/Stepper/SubTabNavigationStepper/SubTabNavigationStepper';
import PatientTreatmentPageWrapper from './PatientTreatmentPageWrapper';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import NetteApiClient from '../../../../Services/NetteApiClient';
import EPrescriptionWrapper from '../../../EPrescription/Components/EPrescriptionWrapper';
import ElectronicPrescriptionDetailWrapper from '../../../Prescription/Components/ElectronicPrescriptionDetail/ElectronicPrescriptionDetailWrapper';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { ConversationFolderEnum } from '../../../../graphql/graphql';
import PermanentMedication from '../../../EPrescription/Components/PermanentMedication/PermanentMedication';

interface PatientTreatmentPrescriptionProps {}

const PatientTreatmentPrescription: React.FC<
	PatientTreatmentPrescriptionProps
> = props => {
	const { patientId } = usePatientTreatmentContext();
	const { isLatam, links, isMou } = useAppContext();
	const [showCreatePrescription, setShowCreatePrescription] = useState(false);
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const { isAllowed } = useAclContext();

	const handleEReceptModal = () => {
		if (!isLatam) {
			if (!document.getElementById('snippet--erecept')) {
				const eReceptSnippet = document.createElement('div');
				eReceptSnippet.setAttribute('id', 'snippet--erecept');
				document.body.appendChild(eReceptSnippet);
			}
			NetteApiClient.get(
				links.treatment.prescriptionModal.replace('__PATIENT__', patientId),
			);
		} else {
			setShowCreatePrescription(true);
		}
	};

	useEffect(() => {
		const tempSteps: NavigationStepperItemType[] = [];

		if (isAllowed('erecept_erecept')) {
			const handleAfterPrescriptionCreated = (
				params: NavigationStepperComponentParams,
			) => {
				params.setActiveStepPath('issuedEprescriptions');
			};

			tempSteps.push({
				name: 'patientTreatment.prescription.create.title',
				key: 'create',
				disabled: isMou,
				component: params =>
					isLatam ? (
						<ScrollContainer>
							<div className='p-4'>
								<ElectronicPrescriptionDetailWrapper patientId={patientId} />
							</div>
						</ScrollContainer>
					) : (
						<EPrescriptionWrapper
							patientId={patientId}
							onPrescriptionCreated={() =>
								handleAfterPrescriptionCreated(params)
							}
							setIsLoading={params.setIsLoading}
						/>
					),
			});
			tempSteps.push({
				name: 'patientTreatment.prescription.issuedEprescriptions.title',
				key: 'issuedEprescriptions',
				disabled: isMou,
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<EReceptList
							patientId={patientId}
							setIsLoading={params.setIsLoading}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}
		if (isAllowed('erecept_lekovyzaznam')) {
			tempSteps.push({
				name: 'patientTreatment.prescription.searchMedicalRecord.title',
				key: 'searchMedicalRecord',
				component: (params: NavigationStepperComponentParams) => (
					<PatientTreatmentPageWrapper patientId={patientId}>
						<SearchMedicamentRecord
							patientId={patientId}
							setIsLoading={params.setIsLoading}
						/>
					</PatientTreatmentPageWrapper>
				),
			});
		}

		tempSteps.push({
			name: 'patientTreatment.prescription.permanentMedication.title',
			key: 'pernamentMedication',
			component: (params: NavigationStepperComponentParams) => (
				<PatientTreatmentPageWrapper patientId={patientId}>
					<PermanentMedication patientId={patientId} />
				</PatientTreatmentPageWrapper>
			),
		});

		setSteps(tempSteps);
	}, []);

	if (!steps.length) {
		return <></>;
	}

	return (
		<>
			<SubTabNavigationStepper
				steps={steps}
				initialStepPath={isMou ? 'searchMedicalRecord' : 'issuedEprescriptions'}
				canSkip={true}
			/>
			<ElectronicPrescriptionModal
				opened={showCreatePrescription}
				onClose={() => setShowCreatePrescription(false)}
				patientId={patientId}
			/>
		</>
	);
};

export default PatientTreatmentPrescription;
